import type { FilterChipEntry } from './FilterChipsBar';

export interface FilterChipOption {
  value: string;
  label: string;
  icon?: string;
  iconColor?: string;
  color?: string;
}

interface BuildOptionFilterChipsParams {
  filterKey: string;
  values: string[];
  options: FilterChipOption[];
  onRemove: (value: string) => void;
  /** Prefixes chip label as `${labelPrefix}: ${optionLabel}` (useful for shared Sim/Não options). */
  labelPrefix?: string;
}

export function buildOptionFilterChips({
  filterKey,
  values,
  options,
  onRemove,
  labelPrefix,
}: BuildOptionFilterChipsParams): FilterChipEntry[] {
  if (values.length === 0) {
    return [];
  }

  const optionsByValue = new Map(options.map((option) => [option.value, option]));

  return values.map((value) => {
    const option = optionsByValue.get(value);
    const optionLabel = option?.label ?? value;

    return {
      filterKey,
      value,
      label: labelPrefix ? `${labelPrefix}: ${optionLabel}` : optionLabel,
      icon: option?.icon,
      iconColor: option?.iconColor,
      color: option?.color,
      onRemove,
    };
  });
}

interface BuildSingleValueFilterChipParams {
  filterKey: string;
  value: string;
  options: FilterChipOption[];
  onRemove: (value: string) => void;
  labelPrefix?: string;
  /** Values that should not produce a chip (e.g. empty / "Todos"). */
  ignoreValues?: string[];
}

export function buildSingleValueFilterChip({
  filterKey,
  value,
  options,
  onRemove,
  labelPrefix,
  ignoreValues = [''],
}: BuildSingleValueFilterChipParams): FilterChipEntry | null {
  if (ignoreValues.includes(value)) {
    return null;
  }

  const [chip] = buildOptionFilterChips({
    filterKey,
    values: [value],
    options,
    onRemove,
    labelPrefix,
  });

  return chip ?? null;
}

interface BuildDateRangeFilterChipParams {
  filterKey: string;
  start: string;
  end: string;
  label: string;
  onRemove: () => void;
}

export function buildDateRangeFilterChip({
  filterKey,
  start,
  end,
  label,
  onRemove,
}: BuildDateRangeFilterChipParams): FilterChipEntry | null {
  if (!start || !end) {
    return null;
  }

  return {
    filterKey,
    value: `${start}|${end}`,
    label,
    onRemove: () => onRemove(),
  };
}

export function compactFilterChips(
  ...chipGroups: Array<FilterChipEntry | FilterChipEntry[] | null | undefined>
): FilterChipEntry[] {
  return chipGroups.flatMap((group) => {
    if (!group) {
      return [];
    }

    return Array.isArray(group) ? group : [group];
  });
}
